import classNames from 'classnames';
import React from 'react';
import type { GroupBase, IndicatorsContainerProps } from 'react-select';
import { components } from 'react-select';
import alertIcon from '../../icons/non-prefilled/16x16/alert.svg';
import SVGIcon from '../SVGIcon';
import type { OptionTypeBase, SelectProps } from '../Select';
import Select from '../Select';
import Tooltip from '../Tooltip';
import { useIsFieldSetDisabled } from './FieldSet';
import * as styles from './SelectControl.module.scss';

type ExtraSelectProps = {
  error?: string;
};

const IndicatorsContainer = <Option, IsMulti extends boolean>(
  props: IndicatorsContainerProps<Option, IsMulti>,
) => {
  const { error } = props.selectProps as ExtraSelectProps;
  return (
    <div className={styles.indicatorsContainer}>
      {error && (
        <Tooltip
          display="block"
          content={error}
          danger
          offset={5}
          enabledForSmallSize
        >
          <div
            onTouchEnd={(e) => {
              e.stopPropagation();
            }}
            className={styles.errorIconContainer}
          >
            <SVGIcon className={styles.errorIcon}>{alertIcon}</SVGIcon>
          </div>
        </Tooltip>
      )}
      <components.IndicatorsContainer {...props} />
    </div>
  );
};

export type SelectControlProps<
  Option extends OptionTypeBase,
  IsMulti extends boolean = boolean,
  IsClearable extends boolean = boolean,
> = Omit<
  SelectProps<Option, IsMulti, true, GroupBase<Option>, IsClearable>,
  'creatable'
> &
  ExtraSelectProps & {
    isMulti?: IsMulti;
    label?: React.ReactNode;
    disabled?: boolean;
  };

const SelectControl = <
  Option extends OptionTypeBase,
  IsMulti extends boolean = false,
  IsClearable extends boolean = false,
>({
  label,
  error,
  disabled,
  ...inputProps
}: SelectControlProps<Option, IsMulti, IsClearable>) => {
  const isFieldSetDisabled = useIsFieldSetDisabled();
  const isDisabled = disabled || isFieldSetDisabled;

  // This helps avoid using @ts-expect-error but gives us typings
  // See https://react-select.com/components#defining-components
  // Custom Component with selectProps Example
  const extraSelectProps: ExtraSelectProps = {
    error: isDisabled ? undefined : error,
  };

  return (
    <label>
      {!!label && <div className={styles.label}>{label}</div>}
      <div className={styles.inputContainer}>
        <Select<Option, IsMulti, true, GroupBase<Option>, IsClearable>
          className={classNames(styles.select, {
            [styles.error]: error && !isDisabled,
          })}
          blurInputOnSelect
          simpleValue
          isDisabled={isDisabled}
          {...inputProps}
          components={{ IndicatorsContainer, ...inputProps.components }}
          {...extraSelectProps}
        />
      </div>
    </label>
  );
};

export default SelectControl;
